import { useState, useEffect, useCallback } from 'react'
import { httpsCallable } from 'firebase/functions'
import { doc, onSnapshot } from 'firebase/firestore'
import { functions, db } from '@/lib/firebase'
import { useAuth } from '@/hooks/useAuth'

interface TelegramLinkState {
  linked: boolean
  loading: boolean
  linkCode: string | null
  botDeepLink: string | null
  generating: boolean
}

export function useTelegramLink() {
  const { user } = useAuth()
  const [state, setState] = useState<TelegramLinkState>({
    linked: false,
    loading: true,
    linkCode: null,
    botDeepLink: null,
    generating: false,
  })

  // Listen to user doc for real-time telegramChatId changes
  useEffect(() => {
    if (!user?.uid) {
      setState(prev => ({ ...prev, loading: false, linked: false }))
      return
    }

    const userDocRef = doc(db, 'users', user.uid)
    const unsubscribe = onSnapshot(
      userDocRef,
      (snapshot) => {
        const data = snapshot.data()
        const linked = !!(data?.telegramChatId)
        setState(prev => ({ ...prev, linked, loading: false }))
      },
      (error) => {
        console.error('Error listening to user doc:', error)
        setState(prev => ({ ...prev, loading: false }))
      }
    )

    return () => unsubscribe()
  }, [user?.uid])

  const generateLinkCode = useCallback(async () => {
    setState(prev => ({ ...prev, generating: true }))
    try {
      const fn = httpsCallable(functions, 'generateTelegramLinkCode')
      const result = await fn({})
      const data = result.data as { code: string; botDeepLink: string }
      setState(prev => ({
        ...prev,
        linkCode: data.code,
        botDeepLink: data.botDeepLink,
        generating: false,
      }))
      return data
    } catch (error) {
      console.error('Failed to generate link code:', error)
      setState(prev => ({ ...prev, generating: false }))
      throw error
    }
  }, [])

  const unlinkTelegram = useCallback(async () => {
    try {
      const fn = httpsCallable(functions, 'unlinkTelegram')
      await fn({})
      setState(prev => ({
        ...prev,
        linked: false,
        linkCode: null,
        botDeepLink: null,
      }))
    } catch (error) {
      console.error('Failed to unlink Telegram:', error)
      throw error
    }
  }, [])

  return {
    linked: state.linked,
    loading: state.loading,
    linkCode: state.linkCode,
    botDeepLink: state.botDeepLink,
    generating: state.generating,
    generateLinkCode,
    unlinkTelegram,
  }
}
